import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, Video, MapPin, Loader2, CheckCircle2, X, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatLocalizedDate } from '@/lib/localizedDate';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';

interface SecondInterviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateName: string;
  onSubmit: (details: {
    interview_type: 'online' | 'face_to_face';
    scheduled_date: string;
    scheduled_time: string;
    meeting_link?: string;
    location_link?: string;
    attendee_emails?: string[];
  }) => void;
  isPending?: boolean;
  onSkipToSelected?: () => void;
}

export function SecondInterviewDialog({
  open, onOpenChange, candidateName, onSubmit, isPending, onSkipToSelected,
}: SecondInterviewDialogProps) {
  const { t, i18n } = useTranslation();
  const [interviewType, setInterviewType] = useState<'online' | 'face_to_face'>('online');
  const [date, setDate] = useState<Date>();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [time, setTime] = useState('');
  const [meetingLink, setMeetingLink] = useState('');
  const [locationLink, setLocationLink] = useState('');
  const [attendeeEmails, setAttendeeEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const isValid = date && time && (
    (interviewType === 'online' && meetingLink.trim()) ||
    (interviewType === 'face_to_face' && locationLink.trim())
  );
  const hasDraft = interviewType !== 'online' ||
    !!date ||
    !!time.trim() ||
    !!meetingLink.trim() ||
    !!locationLink.trim() ||
    attendeeEmails.length > 0 ||
    !!emailInput.trim();

  const resetDraft = () => {
    setDatePickerOpen(false);
    setInterviewType('online');
    setDate(undefined);
    setTime('');
    setMeetingLink('');
    setLocationLink('');
    setAttendeeEmails([]);
    setEmailInput('');
  };

  const requestClose = () => {
    if (isPending) return;
    setDatePickerOpen(false);
    if (hasDraft) {
      setDiscardConfirmOpen(true);
      return;
    }
    onOpenChange(false);
  };

  const discardAndClose = () => {
    resetDraft();
    setDiscardConfirmOpen(false);
    onOpenChange(false);
  };

  const addEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !attendeeEmails.includes(email)) {
      setAttendeeEmails((prev) => [...prev, email]);
      setEmailInput('');
    }
  };

  const removeEmail = (email: string) => {
    setAttendeeEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleSubmit = () => {
    if (!date || !time) return;
    onSubmit({
      interview_type: interviewType,
      scheduled_date: [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
      ].join('-'),
      scheduled_time: time,
      meeting_link: interviewType === 'online' ? meetingLink.trim() : undefined,
      location_link: interviewType === 'face_to_face' ? locationLink.trim() : undefined,
      attendee_emails: attendeeEmails.length > 0 ? attendeeEmails : undefined,
    });
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(nextOpen) => { if (nextOpen) onOpenChange(true); else requestClose(); }}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md grid-cols-1 overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="text-base">{t('hire.secondInterview.title', { candidateName })}</DialogTitle>
          <DialogDescription className="text-xs">
            {t('hire.secondInterview.description', { candidateName })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {/* Interview Type */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('hire.secondInterview.interviewType')}</Label>
            <RadioGroup value={interviewType} onValueChange={(v) => setInterviewType(v as 'online' | 'face_to_face')} className="flex gap-4">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="online" id="online" />
                <Label htmlFor="online" className="flex items-center gap-1.5 cursor-pointer text-sm">
                  <Video className="w-4 h-4 text-primary" /> {t('hire.secondInterview.online')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="face_to_face" id="face_to_face" />
                <Label htmlFor="face_to_face" className="flex items-center gap-1.5 cursor-pointer text-sm">
                  <MapPin className="w-4 h-4 text-primary" /> {t('hire.secondInterview.physical')}
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('hire.secondInterview.date')}</Label>
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn('w-full justify-start text-start font-normal', !date && 'text-muted-foreground')}>
                  <CalendarIcon className="me-2 h-4 w-4" />
                  {date ? formatLocalizedDate(date, 'PPP', i18n.language) : t('hire.secondInterview.pickDate')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(selectedDate) => {
                    setDate(selectedDate);
                    if (selectedDate) setDatePickerOpen(false);
                  }}
                  disabled={(d) => d < new Date()}
                  initialFocus
                  className={cn('p-3 pointer-events-auto')}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('hire.secondInterview.time')}</Label>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="h-10" />
          </div>

          {/* Conditional Link */}
          {interviewType === 'online' ? (
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('hire.secondInterview.meetingLink')}</Label>
              <Input
                placeholder={t('hire.secondInterview.meetingPlaceholder')}
                value={meetingLink}
                onChange={(e) => setMeetingLink(e.target.value)}
                className="h-10"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('hire.secondInterview.locationLink')}</Label>
              <Input
                placeholder={t('hire.secondInterview.locationPlaceholder')}
                value={locationLink}
                onChange={(e) => setLocationLink(e.target.value)}
                className="h-10"
              />
            </div>
          )}

          {/* Attendee Emails */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('hire.secondInterview.alsoInvite')}</Label>
            <div className="flex gap-2">
              <Input
                placeholder="email@example.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                className="h-9 text-sm"
              />
              <Button type="button" variant="outline" size="sm" className="h-9 px-2.5 shrink-0" onClick={addEmail}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {attendeeEmails.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {attendeeEmails.map((email) => (
                  <span key={email} className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                    {email}
                    <button type="button" onClick={() => removeEmail(email)} className="hover:text-destructive transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-4">
          {onSkipToSelected && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl text-success border-success/30 hover:bg-success/5 hover:text-success"
              disabled={isPending}
              onClick={() => {
                onSkipToSelected();
                onOpenChange(false);
              }}
            >
              <CheckCircle2 className="w-4 h-4 me-1.5" /> {t('hire.secondInterview.skip')}
            </Button>
          )}
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={requestClose} disabled={isPending}>{t('common.cancel')}</Button>
          <Button size="sm" onClick={handleSubmit} disabled={!isValid || isPending}>
            {isPending && <Loader2 className="w-4 h-4 me-1.5 animate-spin" />}
            {t('hire.secondInterview.scheduleSend')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    <AlertDialog open={discardConfirmOpen} onOpenChange={setDiscardConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('hire.secondInterview.discardDialog.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('hire.secondInterview.discardDialog.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('hire.secondInterview.discardDialog.keepEditing')}</AlertDialogCancel>
          <AlertDialogAction onClick={discardAndClose}>
            {t('hire.secondInterview.discardDialog.discard')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
